/**
 * Onboarding Tiers — Competitor vs Casual
 *
 * Competitor: Full baseline (5 lifts), ranked immediately, sees leaderboard
 * Casual:     Skip baseline, explore app first, prompted to baseline later
 *
 * The tier choice is stored in profiles.onboarding_tier and influences:
 *   - Whether baseline screen is shown during onboarding
 *   - Whether the home screen shows baseline checklist prominently
 *   - Whether rank/leaderboard is accessible immediately
 */

import { supabase } from './supabase';
import { createLogger } from './logger';

const log = createLogger('ONBOARDING_TIER');

export type OnboardingTier = 'competitor' | 'casual';

export interface TierConfig {
  tier: OnboardingTier;
  label: string;
  emoji: string;
  description: string;
  /** Whether to show baseline lifts during onboarding */
  showBaseline: boolean;
  /** Whether to immediately show leaderboard access */
  showLeaderboard: boolean;
  /** CTA text on the tier selection screen */
  ctaText: string;
}

export const TIER_CONFIGS: Record<OnboardingTier, TierConfig> = {
  competitor: {
    tier: 'competitor',
    label: 'COMPETITOR',
    emoji: '⚔️',
    description: 'I want to compete. Rank me against others at my gym.',
    showBaseline: true,
    showLeaderboard: true,
    ctaText: 'LET\'S GO',
  },
  casual: {
    tier: 'casual',
    label: 'EXPLORER',
    emoji: '🏋️',
    description: 'I want to track my lifts. I\'ll compete when I\'m ready.',
    showBaseline: false,
    showLeaderboard: false,
    ctaText: 'START LOGGING',
  },
};

/** Save the selected tier to the user's profile */
export async function setOnboardingTier(
  profileId: string,
  tier: OnboardingTier,
): Promise<boolean> {
  const { error } = await supabase
    .from('profiles')
    .update({ onboarding_tier: tier })
    .eq('id', profileId);

  if (error) {
    log.error('setOnboardingTier failed:', error);
    return false;
  }
  return true;
}

/** Get the user's onboarding tier (defaults to competitor for existing users) */
export async function getOnboardingTier(profileId: string): Promise<OnboardingTier> {
  const { data } = await supabase
    .from('profiles')
    .select('onboarding_tier')
    .eq('id', profileId)
    .maybeSingle();

  return (data?.onboarding_tier as OnboardingTier) || 'competitor';
}
